// Global Instructions Rule Applied!
import React, { useCallback, type SyntheticEvent } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { Tabs } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { selectTabs, selectActiveTabKey, setActiveTab, removeTab } from '../../../store/slices/tabSlice'
import { TabContentWrapper } from './TabContentWrapper.tsx'
import { getIconForModule, renderAppIcon } from '../../../ui_configs/iconMap.tsx'

/**
 * Generic Tab Navigation Component
 *
 * Features:
 * - Integrates with Redux tab state
 * - Supports closeable and non-closeable tabs
 * - Handles routing navigation
 * - Ant Design styled tabs
 * - Configurable tab rendering
 */
export function TabNavigation() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const tabs = useSelector(selectTabs)
  const activeTabKey = useSelector(selectActiveTabKey)

  // Flag to prevent useTabManager interference during tab closing
  const isClosingTab = React.useRef(false)

  // Flag to prevent useTabManager interference during user-initiated tab changes
  const isUserChangingTab = React.useRef(false)

  // Handle tab change
  const handleTabChange = useCallback(
    (key: string) => {
      const tab = tabs.find((t) => t.key === key)
      if (tab) {
        // Set flag to prevent useTabManager interference
        isUserChangingTab.current = true
        window.isUserChangingTab = true

        dispatch(setActiveTab({ key }))

        // Navigate to the tab's path if it's different from current location
        if (tab.path !== location.pathname) {
          navigate(tab.path, { replace: true })
        }

        // Clear the flag after navigation completes
        setTimeout(() => {
          isUserChangingTab.current = false
          window.isUserChangingTab = false
        }, 100)
      }
    },
    [tabs, dispatch, navigate, location.pathname]
  )

  // Handle tab close
  const handleTabClose = useCallback(
    (key: string, event: SyntheticEvent) => {
      // Prevent the tab from being activated when closing
      event.stopPropagation()

      const tab = tabs.find((t) => t.key === key)
      if (tab && tab.closeable) {
        // Set flag to prevent useTabManager interference
        isClosingTab.current = true
        window.isClosingTab = true

        // Remove the tab
        dispatch(removeTab({ key }))

        // If this was the active tab and there are other tabs, navigate immediately
        if (key === activeTabKey && tabs.length > 1) {
          // Find what the new active tab will be after removal
          const tabIndex = tabs.findIndex((t) => t.key === key)
          const remainingTabs = tabs.filter((t) => t.key !== key)

          if (remainingTabs.length > 0) {
            const newActiveIndex = Math.max(0, tabIndex - 1)
            const newActiveTab = remainingTabs[newActiveIndex]

            // Navigate immediately to avoid any async issues
            if (newActiveTab) {
              // Use setTimeout to ensure the tab removal is processed first
              setTimeout(() => {
                if (location.pathname !== newActiveTab.path) {
                  navigate(newActiveTab.path, { replace: true })
                }
                // Clear the flag after navigation
                setTimeout(() => {
                  isClosingTab.current = false
                  window.isClosingTab = false
                }, 100)
              }, 1)
            }
          }
        } else {
          // Clear the flag immediately if not navigating
          setTimeout(() => {
            isClosingTab.current = false
            window.isClosingTab = false
          }, 100)
        }
      }
    },
    [tabs, activeTabKey, dispatch, navigate, location.pathname]
  )

  // Convert tabs to Ant Design format - just the tab headers, content managed separately
  const tabItems = tabs.map((tab) => ({
    key: tab.key,
    label: (
      <div className='flex items-center gap-2 min-w-0'>
        <span className='text-sm text-gray-600 flex-shrink-0 flex items-center'>
          {renderAppIcon(getIconForModule(tab.module), { width: '1em', height: '1em' })}
        </span>
        <span className='truncate text-sm font-medium'>{tab.title}</span>
        {tab.closeable && (
          <div
            className='flex items-center justify-center w-5 h-5 ml-1 rounded hover:bg-red-50 cursor-pointer transition-all duration-200'
            onClick={(e) => handleTabClose(tab.key, e)}
            onMouseDown={(e) => e.stopPropagation()} // Prevent tab activation on mousedown
            role='button'
            aria-label={`Close ${tab.title} tab`}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleTabClose(tab.key, e)
              }
            }}
          >
            <FontAwesomeIcon
              icon={faTimes}
              className='text-xs text-gray-400 hover:text-red-600 pointer-events-none transition-colors duration-200'
            />
          </div>
        )}
      </div>
    ),
    closable: false, // We handle closing manually for better control
    destroyOnHidden: false
  }))

  // Don't render if no tabs
  if (tabs.length === 0) {
    return null
  }

  return (
    <>
      <div className='flex flex-col h-full'>
        {/* Tab headers */}
        <div className='bg-gray-50 border-b border-gray-300 shadow-sm flex-shrink-0'>
          <div className='px-6 pt-1'>
            <Tabs
              type='card'
              activeKey={activeTabKey ?? undefined}
              onChange={handleTabChange}
              items={tabItems}
              size='middle'
              className='reservoir-tabs'
              style={{ marginBottom: 0 }}
              destroyOnHidden={false}
            />
          </div>
        </div>

        {/* Tab content */}
        <div className='flex-1 min-h-0'>
          <TabContentWrapper />
        </div>
      </div>
    </>
  )
}
